<template>
  <div class="relative h-full overflow-hidden">
    <div
      class="relative h-full"
      :class="{ 'transition-transform duration-300 ease-in-out': isAnimating }"
      :style="{
        transform: `translateX(${translateX / 16}rem)`
      }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="endDrag"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="endDrag"
      @mouseleave="endDrag"
      @click.capture="handleClick"
    >
      <div
        v-if="$slots.left"
        :class="[
          'absolute top-0 left-0 w-14 h-full flex items-center justify-center',
          'before:absolute before:top-0 before:bottom-0 before:left-2 before:w-[999px] before:-translate-x-full before:bg-g-500'
        ]"
        :style="{
          transform: `translateX(${Math.min(-56, -56 + translateX) / 16}rem)`
        }"
        @click="handleLeftClick"
      >
        <slot name="left"></slot>
      </div>

      <div class="w-full h-full relative">
        <slot name="default"></slot>
      </div>

      <div
        v-if="$slots.right"
        :class="[
          'absolute top-0 right-0 w-14 h-full flex items-center justify-center',
          'before:absolute before:top-0 before:bottom-0 before:right-1 before:w-[999px] before:translate-x-full before:bg-r-500'
        ]"
        :style="{
          transform: `translateX(${Math.max(56, 56 + translateX) / 16}rem)`
        }"
        @click="handleRightClick"
      >
        <slot name="right"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, useSlots, watch } from 'vue'

const props = defineProps<{
  isDisabled?: boolean
}>()

const slots = useSlots()

const startX = ref(0)
const startTranslateX = ref(0)
const translateX = ref(0)

const isAnimating = ref(false)
const isDragging = ref(false)
const hasDragged = ref(false)

const resistanceRatio = 0.3

function handleTouchStart(event: TouchEvent) {
  if (props.isDisabled) return
  startDrag(event.touches[0].clientX)
}

function handleMouseDown(event: MouseEvent) {
  if (props.isDisabled) return
  startDrag(event.clientX)
}

function startDrag(clientX: number) {
  if (props.isDisabled) return
  isDragging.value = true
  isAnimating.value = false
  hasDragged.value = false
  startX.value = clientX
  startTranslateX.value = translateX.value
}

function handleTouchMove(event: TouchEvent) {
  if (!isDragging.value || props.isDisabled) return
  updateDrag(event.touches[0].clientX)
}

function handleMouseMove(event: MouseEvent) {
  if (!isDragging.value || props.isDisabled) return
  updateDrag(event.clientX)
}

function updateDrag(clientX: number) {
  if (props.isDisabled) return
  const deltaX = clientX - startX.value

  // Mark as dragged if movement is significant
  if (Math.abs(deltaX) > 5) {
    hasDragged.value = true
  }

  let newTranslateX = startTranslateX.value + deltaX

  // Apply resistance when dragging beyond boundaries
  const maxLeftSwipe = slots.left ? 56 : 0
  const maxRightSwipe = slots.right ? -56 : 0

  if (newTranslateX > maxLeftSwipe) {
    const excess = newTranslateX - maxLeftSwipe
    newTranslateX = maxLeftSwipe + excess * resistanceRatio
  } else if (newTranslateX < maxRightSwipe) {
    const excess = Math.abs(newTranslateX - maxRightSwipe)
    newTranslateX = maxRightSwipe - excess * resistanceRatio
  }

  translateX.value = newTranslateX
}

function endDrag(ev: MouseEvent | TouchEvent) {
  if (!isDragging.value || props.isDisabled) return

  isDragging.value = false
  isAnimating.value = true

  if (hasDragged.value) {
    if (ev.cancelable) {
      ev.preventDefault()
    }
    ev.stopImmediatePropagation?.()
    ev.stopPropagation()
  }

  // Determine final position based on swipe distance and velocity
  const threshold = 28 // Half of action width

  if (translateX.value > threshold && slots.left) {
    // Snap to left action
    translateX.value = 56
  } else if (translateX.value < -threshold && slots.right) {
    // Snap to right action
    translateX.value = -56
  } else {
    // Snap back to center
    translateX.value = 0
  }

  // Reset animation flag after transition
  setTimeout(() => {
    isAnimating.value = false
  }, 300)
}

function handleClick(event: Event) {
  // Prevent click events if the user just finished dragging
  if (props.isDisabled) return
  if (hasDragged.value) {
    event.preventDefault()
    event.stopPropagation()
    return false
  }
}

function handleLeftClick(event: Event) {
  event.stopPropagation()
  close()
}

function handleRightClick(event: Event) {
  event.stopPropagation()
  close()
}

function close() {
  isAnimating.value = true
  translateX.value = 0

  setTimeout(() => {
    isAnimating.value = false
  }, 300)
}

watch(
  () => props.isDisabled,
  () => {
    if (props.isDisabled) {
      close()
    }
  }
)

defineExpose({
  close
})
</script>
